import React from "react";
import { styled } from "@linaria/react";
import HighchartsComponent from "@ui/Highcharts";
import ChartCard from "@ui/Highcharts/ui/Card";
import PeriodSelector from "../../../Analysis/PeriodSelector";
import Selector from "@ui/Selector";
import InfoTooltip from "../../../Chart/InfoTooltip";
import CommonLegend from "../../../Chart/Legend/CommonLegend";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import FailureLegend, { useLegendGroupClick } from "../../../Chart/Legend/FailureLegend";
import useSelectedAnalyses from "../../../Analysis/useSelectedAnalyses";
import { INTERNAL_GAP_ID } from "../../../Chart/series/getInternalGapSerie";
import type { Project } from "../../../../../api/useProjects";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { Setup } from "../../../../../api/useSetups";
import type { Source } from "../../../../../api/useSources";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../api/useManualFailureValues";
import t from "../../../../../i18n";
import ToggleGroupBreaks from "../../../Analysis/ToggleGroupBreaks";
import {
  useDiagnosticWithQuantityNoDataIntervals,
  useHasDiagnosticDataInPeriod,
} from "../../../useNoDataIntervals";
import { useProjectPeriod } from "../../../useProjectPeriod";
import { NO_FAILURES_SERIE_ID } from "../../../Diagnosis/FailureEvolution/getNoFailureSeries";
import useSuspectedFailuresInExplanation from "../../../Diagnosis/useSuspectedFailuresInExplanation";
import useOptions from "./useOptions";
import useSeries from "./useSeries";
import type { Analysis } from "../../../../../api/Analysis";
import { color } from "@ui";
import { FlagLine } from "@ui/IconV2/react";
import { ChartMenu } from "../../../Chart/Menu";
import TogglePublicationDisplay, {
  usePublicationFlagDisplay,
} from "../../../Analysis/TogglePublicationDisplay";
import { useBreaksBetweenGroups } from "../../../Analysis/useBreaksBetweenGroups";
import {
  FullViewportButtonMarker,
  FullViewportButtonMarkerActive,
  FullViewportButtonMarkerInactive,
  FullViewportTrigger,
} from "@ui/FullViewportButton";
import { useSettings } from "../../../../../api/useSettings";
import { Divider } from "../../../Chart/Legend/FailureLegend/Tooltip";
import { useTranslations } from "../../../../../api/useTranslations";
import { useImpactChartType } from "../../../Diagnosis/FailureEvolution/hooks/useImpactChartType";
import { GLOBAL_DIAGNOSIS_CHART } from "../../../Diagnosis/FailureEvolution/hooks/useDiagnosisChartType";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import cx from "classnames";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import { useQuantityIndex, type Quantity } from "../../../../../api/useQuantities";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { generateExportFileName } from "diagnostics-lib/string";
import { UNEXPLAINED_GAP_ID } from "../../../Chart/series/serieIds";
import { useFailureGroupDisplayNameMap } from "../../../../../api/useFailureGroups";
import PeriodZoomAllButton from "../../../Chart/PeriodZoomAllButton";

const HAS_ADDITIONNAL_MESSAGE: string = "additionalMessages";
const containerRightOptions = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};

  &.${HAS_ADDITIONNAL_MESSAGE} {
    align-items: flex-start;
  }
`;

const Wrapper = styled.div`
  display: flex;
  column-gap: 8px;
  align-items: center;
  height: 32px;
`;

const Title = styled.div`
  display: flex;
  column-gap: 40px;
  width: 100%;
`;

const ActionMenu = ({ analyses, project }: { analyses: Analysis[]; project: Project }) => {
  const { selectedAnalyses } = useSelectedAnalyses(project.id, analyses);

  const title = React.useMemo(() => {
    if (selectedAnalyses.length === 0) {
      return t("summary.chart.evolution.noAnalysisPublished");
    }
    return null;
  }, [selectedAnalyses]);

  const flagColor = React.useMemo(() => {
    if (selectedAnalyses.length === 0) return color.titanium600;
    return color.lightNitrogen600;
  }, [selectedAnalyses.length]);

  if (!title) return null;

  return (
    <React.Suspense fallback="">
      <Wrapper>
        <FlagLine width="14px" height="14px" color={flagColor} />
        {title}
      </Wrapper>
    </React.Suspense>
  );
};

interface P {
  project: Project;
  source: Source;
  setups: Setup[];
  activeAnalyses: NonEmptyArray<ActiveAnalysis>;
  quantitiesOfInterest: Quantity[];
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
}

const ChartLegendWrapper = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 1fr;
  height: 400px;
  width: 100%;

  .${FullViewportButtonMarkerActive} &,
  .${FullViewportButtonMarkerInactive} & {
    height: 100%;
  }
`;

const Evolution = ({
  project,
  setups,
  activeAnalyses,
  indexedFailureValues,
  indexedManualFailureValues,
  source,
  quantitiesOfInterest,
}: P) => {
  const settings = useSettings();
  const translations = useTranslations();
  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalyses);
  const analysis = selectedAnalyses[0];
  const analysisSetup = analysis ? getAnalysisSetup(setups, analysis) : null;

  const [chartTypeState, setChartType] = useImpactChartType({
    scope: project.id,
    defaultValue: String(quantitiesOfInterest[0]?.id),
  });

  //Note: Is it still useful to have this environment variable "MTS_ENV.SUMMARY_GLOBAL_IMPACT_OPTION" ?
  const chartType = React.useMemo(
    () =>
      chartTypeState === GLOBAL_DIAGNOSIS_CHART
        ? String(quantitiesOfInterest[0]?.id)
        : chartTypeState,
    [quantitiesOfInterest, chartTypeState],
  );

  const chartTypeOptions = React.useMemo(() => {
    const options = quantitiesOfInterest.map(q => ({
      label: q.name,
      value: String(q.id),
    }));
    return options as NonEmptyArray<{ label: string; value: string }>;
  }, [quantitiesOfInterest]);

  const chartTypeOption = React.useMemo(
    () => chartTypeOptions.find(c => c.value === chartType) || chartTypeOptions[0],
    [chartTypeOptions, chartType],
  );

  const chartTypeDescriptions = React.useMemo(() => {
    const options = [
      {
        label: t("diagnosis.overview.header.option.qoi"),
        value: "QOI",
        description: t("summary.chart.evolution.option.qoi.description", translations),
      },
    ];
    return options;
  }, [translations]);

  const quantityIndex = useQuantityIndex(project.id);

  const qoi = React.useMemo(() => {
    const qoi = quantityIndex[chartType];
    if (!qoi) throw new Error(`QOI ${chartType} not found`);
    return qoi;
  }, [chartType, quantityIndex]);

  const [period, setPeriod, extremes] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });
  const hasDataInPeriod = useHasDiagnosticDataInPeriod(
    activeAnalyses,
    indexedFailureValues,
    period,
  );
  const hasDataInAllPeriod = useHasDiagnosticDataInPeriod(
    activeAnalyses,
    indexedFailureValues,
    extremes,
  );
  const handleZoomToAllPeriod = React.useCallback(
    () => setPeriod({ from: extremes.from, to: extremes.to }),
    [extremes.from, extremes.to, setPeriod],
  );
  const chartRef = React.useRef<Highcharts.Chart>(null);
  const yAxisId = `project-id-${project.id}-qoi-id-${qoi.id}-summary-overview`;
  const [suspectedFailuresIncluded] = useSuspectedFailuresInExplanation({ scope: project.id });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: project.id,
  });
  const series = useSeries({
    project,
    yAxisId,
    setups,
    analyses: activeAnalyses,
    indexedFailureValues,
    indexedManualFailureValues,
    qoi,
    quantityIndex,
    actionAllowed: false,
    includeSuspectedFailure: suspectedFailuresIncluded,
    publicationFlagDisplayed,
  });
  const [visibleSeries, onSerieClick, setSerieVisibility] = useVisibleSeries(
    `summaryOverview-${project.id}`,
    series,
  );
  const failureGroupingEnabled = settings.FAILURE_GROUPING_ENABLED;
  const { groupByFailureDisplayName } = useFailureGroupDisplayNameMap(project.id, {
    enabled: failureGroupingEnabled,
  });

  const handleLegendGroupClick = useLegendGroupClick(visibleSeries, setSerieVisibility);

  const noDataIntervals = useDiagnosticWithQuantityNoDataIntervals(
    activeAnalyses,
    indexedFailureValues,
  );

  const options = useOptions({
    source,
    project,
    chartRef,
    chartTypeOption,
    yAxisId,
    analyses: activeAnalyses,
    indexedFailureValues,
    qoi,
    series: visibleSeries,
  });
  const chartRefs = React.useMemo(() => [chartRef], [chartRef]);
  const renderOtherSeriesTooltip = React.useCallback((serie: Highcharts.SeriesOptions) => {
    switch (serie.id) {
      case NO_FAILURES_SERIE_ID:
        return (
          <>
            <Divider />
            {t("diagnosis.overview.legend.tooltip.no_failure")}
          </>
        );
      case INTERNAL_GAP_ID:
        return (
          <>
            <Divider />
            {t("diagnosis.overview.legend.tooltip.internal_losses")}
          </>
        );
      case UNEXPLAINED_GAP_ID:
        return (
          <>
            <Divider />
            {t("diagnosis.overview.legend.tooltip.unexplained_losses")}
          </>
        );
      default:
        throw new Error("It should not happen");
    }
  }, []);
  const leftOptions = React.useMemo(
    () => (
      <InfoTooltip
        title={t("diagnosis.overview.header.option.title")}
        options={chartTypeDescriptions}
        link="/summary-page"
      >
        <div className={HIDE_ON_WIDGET_EXPORT}>
          <Selector options={chartTypeOptions} value={chartType} onChange={setChartType} />
        </div>
      </InfoTooltip>
    ),
    [chartType, chartTypeDescriptions, chartTypeOptions, setChartType],
  );

  const rightOptions = React.useMemo(
    () => (
      <div className={cx(containerRightOptions, HIDE_ON_WIDGET_EXPORT)}>
        <PeriodZoomAllButton
          visible={!hasDataInPeriod && hasDataInAllPeriod}
          onClick={handleZoomToAllPeriod}
          label={t("chart.period.zoom_all")}
        />
        <PeriodSelector
          project={project}
          source={source}
          analyses={activeAnalyses}
          boundaries="active-analyses"
        />
        <ChartMenu
          marker={!breakBetweenGroups || publicationFlagDisplayed}
          tooltipOverlay={t("chart.display_options.tooltip")}
        >
          <TogglePublicationDisplay projectId={project.id} />
          <ToggleGroupBreaks projectId={project.id} groupables={activeAnalyses} />
        </ChartMenu>
      </div>
    ),
    [
      activeAnalyses,
      breakBetweenGroups,
      handleZoomToAllPeriod,
      hasDataInAllPeriod,
      hasDataInPeriod,
      project,
      publicationFlagDisplayed,
      source,
    ],
  );

  const legend = React.useMemo(
    () => (
      <>
        <CommonLegend
          chartRef={chartRef}
          analyses={activeAnalyses}
          series={series}
          period={period}
          noDataIntervals={noDataIntervals}
          chartType="summary"
          projectId={project.id}
        />
      </>
    ),
    [activeAnalyses, period, noDataIntervals, series, project],
  );
  const children = React.useMemo(
    () => (
      <>
        <ChartLegendWrapper>
          <FailureLegend
            project={project}
            chartRefs={chartRefs}
            series={visibleSeries}
            period={period}
            onLegendItemClick={onSerieClick}
            setup={analysisSetup}
            renderOtherSeriesTooltip={renderOtherSeriesTooltip}
            hideColorPicker
            groupByFailureDisplayName={
              failureGroupingEnabled ? groupByFailureDisplayName : undefined
            }
            onLegendGroupClick={handleLegendGroupClick}
          />
          <HighchartsComponent
            name="Diagnosis overview"
            ref={chartRef}
            options={options}
            selectablePoints={false}
          />
        </ChartLegendWrapper>
      </>
    ),
    [
      groupByFailureDisplayName,
      handleLegendGroupClick,
      analysisSetup,
      chartRefs,
      onSerieClick,
      options,
      period,
      project,
      renderOtherSeriesTooltip,
      visibleSeries,
      failureGroupingEnabled,
    ],
  );

  return (
    <ChartCard
      title={
        <Title>
          {t("summary.chart.evolution.title")}
          <div style={{ marginLeft: "auto" }}>
            <ExportWidgetButton
              fileName={generateExportFileName({
                filename: t("summary.chart.evolution.title"),
                projectName: project.name,
                extension: "png",
              })}
            />
            <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
          </div>
        </Title>
      }
      subtitle={<ActionMenu analyses={selectedAnalyses} project={project} />}
      leftOptions={leftOptions}
      rightOptions={rightOptions}
      legend={legend}
      className={cx(FullViewportButtonMarker, exportWidgetTarget)}
    >
      {children}
    </ChartCard>
  );
};

export default React.memo(Evolution);
